import React, { useEffect, useRef, useState } from 'react';
import Style from './ContactModal.module.scss';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight } from '../ui/Icons';

const EMPTY = { name: '', email: '', subject: '', message: '' };

export default function ContactModal({ open, onClose }) {
    const [fields, setFields] = useState(EMPTY);
    const [botField, setBotField] = useState('');
    const [status, setStatus] = useState('idle'); // idle | sending | done | error
    const [error, setError] = useState('');
    const firstRef = useRef(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => firstRef.current?.focus(), 80);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            // reset shortly after close so the closing animation keeps its content
            const t = setTimeout(() => { setStatus('idle'); setFields(EMPTY); setError(''); }, 300);
            return () => clearTimeout(t);
        }
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && open) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const set = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.value }));

    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email);
    const canSend = fields.name.trim() && validEmail && fields.message.trim() && status !== 'sending';

    const submit = async (e) => {
        e.preventDefault();
        if (botField) return; // honeypot tripped — silently drop
        if (!canSend) return;
        setStatus('sending');
        setError('');
        try {
            const res = await fetch('/.netlify/functions/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...fields, botField }),
            });
            if (!res.ok) {
                let msg = 'could not send — please try LinkedIn';
                try { const b = await res.json(); if (b.error) msg = b.error; } catch { /* keep default */ }
                throw new Error(msg);
            }
            setStatus('done');
        } catch (err) {
            setStatus('error');
            setError(err.message || 'something went wrong');
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className={Style.backdrop}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
                >
                    <motion.div
                        className={Style.panel}
                        role="dialog"
                        aria-modal="true"
                        aria-label="send a message"
                        initial={{ opacity: 0, y: 18, scale: 0.985 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.99 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className={Style.bar}>
                            <span className={Style.dot} />
                            sai@portfolio — compose
                            <button className={Style.esc} onClick={onClose}>esc</button>
                        </div>

                        {status === 'done' ? (
                            <div className={Style.success}>
                                <p className={Style.ok}>✓ 202 Accepted</p>
                                <p className={Style.successMsg}>
                                    message delivered. I read everything and reply from
                                    <br />my personal inbox — usually within a day or two.
                                </p>
                                <button className={Style.sendBtn} onClick={onClose}>close</button>
                            </div>
                        ) : (
                            <form className={Style.form} onSubmit={submit} noValidate>
                                <p className={Style.request}>
                                    <span className={Style.method}>POST</span> /contact
                                    <span className={Style.hint}>your email stays private</span>
                                </p>

                                {/* honeypot — hidden from humans */}
                                <input
                                    type="text"
                                    name="bot-field"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={botField}
                                    onChange={(e) => setBotField(e.target.value)}
                                    className={Style.honeypot}
                                    aria-hidden="true"
                                />

                                <label className={Style.field}>
                                    <span>from.name</span>
                                    <input
                                        ref={firstRef}
                                        type="text"
                                        name="name"
                                        placeholder="your name"
                                        value={fields.name}
                                        onChange={set('name')}
                                        required
                                    />
                                </label>

                                <label className={Style.field}>
                                    <span>from.email</span>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="you@company.com"
                                        value={fields.email}
                                        onChange={set('email')}
                                        required
                                    />
                                </label>

                                <label className={Style.field}>
                                    <span>subject</span>
                                    <input
                                        type="text"
                                        name="subject"
                                        placeholder="what's this about?"
                                        value={fields.subject}
                                        onChange={set('subject')}
                                    />
                                </label>

                                <label className={`${Style.field} ${Style.fieldArea}`}>
                                    <span>message</span>
                                    <textarea
                                        name="message"
                                        rows={4}
                                        placeholder="tell me what you're building…"
                                        value={fields.message}
                                        onChange={set('message')}
                                        required
                                    />
                                </label>

                                {status === 'error' && (
                                    <p className={Style.errorLine}>✕ {error} — or email via LinkedIn instead</p>
                                )}

                                <div className={Style.actions}>
                                    <button type="submit" className={Style.sendBtn} disabled={!canSend}>
                                        {status === 'sending'
                                            ? '⟳ sending…'
                                            : <>send request <ArrowUpRight size={13} /></>}
                                    </button>
                                    <span className={Style.note}>encrypted in transit · never shared</span>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
